import { GUEST_TELEMETRY_SCHEMA, sanitizeProperties } from "./schema.js";
import { errorClass, stackFingerprint, textFingerprint } from "./env.js";

const BATCH_SIZE = 20;
const MAX_QUEUED = 100;
const MAX_EVENTS_PER_PAGE_LOAD = 300;
const MAX_JS_ERROR_FINGERPRINTS = 10;
const JS_ERROR_CLASSES = GUEST_TELEMETRY_SCHEMA.events.js_error.properties.class.enum;
const FINGERPRINT = /^[0-9a-f]{16}$/;
const MAX_COUNT = 1000;
const STALL_INTERVAL_MS = 30_000;
const FLUSH_INTERVAL_MS = 10_000;
const CONTENT_TYPE = "text/plain;charset=UTF-8";

// `VITE_GUEST_TELEMETRY=off` at build time switches telemetry off. Outside Vite the flag is absent.
const MEDIA_TAGS = ["AUDIO", "VIDEO", "SOURCE", "TRACK"];

function buildTelemetryOff() {
  try {
    return import.meta.env.VITE_GUEST_TELEMETRY === "off";
  } catch {
    return false;
  }
}

// Global Privacy Control, Do Not Track (on the navigator or as the legacy window property) and automation opt out.
function optedOut(win) {
  const nav = win.navigator ?? {};
  return nav.globalPrivacyControl === true ||
    nav.doNotTrack === "1" ||
    win.doNotTrack === "1" ||
    nav.webdriver === true;
}

function randomHex(bytes) {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return Array.from(buffer, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * One instance per page load. Nothing is stored: the page-load id lives in memory,
 * and a batch holds only allowlisted names and enum values.
 */
export function createTelemetry({
  win = window,
  fetchImpl = (url, init) => fetch(url, init),
  now = () => performance.now(),
  setTimer = setTimeout,
  clearTimer = clearTimeout,
  enabled = true,
  buildOff = buildTelemetryOff(),
  endpoint = "/api/web/telemetry",
  flushMs = FLUSH_INTERVAL_MS,
} = {}) {
  const active = enabled && !buildOff && !optedOut(win);
  const pageLoadId = randomHex(16);
  const queue = [];
  const aggregates = new Map();
  const fingerprints = new Set();
  const stallTimes = new Map();
  let emitted = 0;
  let sessionEndedSent = false;
  let timer = null;
  let stopped = false;
  let listeners = [];

  const live = () => active && !stopped;

  // A rejected event takes no sequence number. Sequence numbers stay below 300.
  function push(event) {
    if (queue.length >= MAX_QUEUED || emitted >= MAX_EVENTS_PER_PAGE_LOAD) return false;
    queue.push({ ...event, s: emitted });
    emitted += 1;
    return true;
  }

  function enqueue(event) {
    if (!push(event)) return;
    if (queue.length >= BATCH_SIZE) send(queue.splice(0, BATCH_SIZE));
    schedule();
  }

  // One timer covers everything waiting. It starts with the first waiting event and stops when nothing waits.
  function schedule() {
    if (queue.length === 0 && aggregates.size === 0) {
      if (timer !== null) {
        clearTimer(timer);
        timer = null;
      }
      return;
    }
    if (timer === null) {
      timer = setTimer(() => {
        timer = null;
        flush();
      }, flushMs);
    }
  }

  function materialize() {
    for (const aggregate of aggregates.values()) {
      for (let remaining = aggregate.total; remaining > 0; remaining -= MAX_COUNT) {
        push({ n: aggregate.name, p: { ...aggregate.props, count: Math.min(remaining, MAX_COUNT) } });
      }
    }
    aggregates.clear();
  }

  function send(events, { beacon = false } = {}) {
    const body = JSON.stringify({ v: 1, id: pageLoadId, events });
    if (beacon && sendBeacon(body)) return;
    post(body);
  }

  function sendBeacon(body) {
    if (typeof win.navigator?.sendBeacon !== "function") return false;
    try {
      return win.navigator.sendBeacon(endpoint, new Blob([body], { type: CONTENT_TYPE })) === true;
    } catch {
      return false;
    }
  }

  // Failures are ignored and never retried. A 429 stops telemetry for the rest of the page load.
  function post(body) {
    let request;
    try {
      request = fetchImpl(endpoint, {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": CONTENT_TYPE },
        body,
      });
    } catch {
      return;
    }
    Promise.resolve(request)
      .then((response) => {
        if (response?.status === 429) stop();
      })
      .catch(() => {});
  }

  function stop() {
    stopped = true;
    queue.length = 0;
    aggregates.clear();
    if (timer !== null) {
      clearTimer(timer);
      timer = null;
    }
  }

  function track(name, properties) {
    if (!live()) return;
    const sanitized = sanitizeProperties(name, properties);
    if (sanitized) enqueue({ n: name, p: sanitized });
  }

  // Repeats of the same event and properties add up, and go out as one event with a count.
  function count(name, properties, n = 1) {
    if (!live() || !Number.isSafeInteger(n) || n < 1) return;
    const sanitized = sanitizeProperties(name, properties, { omitCount: true });
    if (!sanitized) return;
    const key = `${name}|${JSON.stringify(sanitized)}`;
    const existing = aggregates.get(key);
    if (existing) existing.total += n;
    else aggregates.set(key, { name, props: sanitized, total: n });
    schedule();
  }

  function flush({ beacon = false } = {}) {
    if (!live()) return;
    if (timer !== null) {
      clearTimer(timer);
      timer = null;
    }
    materialize();
    while (queue.length > 0) send(queue.splice(0, BATCH_SIZE), { beacon });
  }

  function sessionEnded(reason, where) {
    if (!live() || sessionEndedSent) return;
    const sanitized = sanitizeProperties("session_ended", { reason, where });
    if (!sanitized) return;
    sessionEndedSent = true;
    enqueue({ n: "session_ended", p: sanitized });
  }

  function reportJsError(kind, fingerprint, source = "page") {
    if (!live()) return;
    if (!fingerprints.has(fingerprint)) {
      if (fingerprints.size >= MAX_JS_ERROR_FINGERPRINTS) return;
      fingerprints.add(fingerprint);
    }
    count("js_error", { class: kind, fingerprint, source });
  }

  // A {class, fingerprint} the media service worker posted (workerClient). Nothing else of it is read.
  function serviceWorkerError(data) {
    const fingerprint = data?.fingerprint;
    if (typeof fingerprint !== "string" || !FINGERPRINT.test(fingerprint)) return;
    const kind = JS_ERROR_CLASSES.includes(data?.class) ? data.class : "other";
    reportJsError(kind, fingerprint, "service_worker");
  }

  // At most one stall per element per 30 seconds. Any value that identifies the element works as the key.
  function allowStall(key) {
    if (!live()) return false;
    const at = now();
    const last = stallTimes.get(key);
    if (last !== undefined && at - last < STALL_INTERVAL_MS) return false;
    stallTimes.set(key, at);
    return true;
  }

  function installGlobalHandlers() {
    if (!live() || listeners.length > 0) return;
    const doc = win.document;
    const onError = (event) => {
      const target = event?.target;
      if (target && target !== win && typeof target.tagName === "string") {
        // Media failures are counted by the playback events, not as script or resource errors.
        if (MEDIA_TAGS.includes(target.tagName.toUpperCase())) return;
        textFingerprint(`resource:${target.tagName.toLowerCase()}`)
          .then((fingerprint) => reportJsError("resource", fingerprint), () => {});
        return;
      }
      stackFingerprint(event?.error?.stack)
        .then((fingerprint) => reportJsError(errorClass(event?.error), fingerprint), () => {});
    };
    const onRejection = (event) => {
      stackFingerprint(event?.reason?.stack)
        .then((fingerprint) => reportJsError("unhandled_rejection", fingerprint), () => {});
    };
    const onPageHide = () => flush({ beacon: true });
    const onVisibilityChange = () => {
      if (win.document?.visibilityState === "hidden") flush({ beacon: true });
    };
    win.addEventListener("error", onError, true);
    win.addEventListener("unhandledrejection", onRejection);
    win.addEventListener("pagehide", onPageHide);
    doc?.addEventListener?.("visibilitychange", onVisibilityChange);
    listeners = [
      () => win.removeEventListener("error", onError, true),
      () => win.removeEventListener("unhandledrejection", onRejection),
      () => win.removeEventListener("pagehide", onPageHide),
      () => doc?.removeEventListener?.("visibilitychange", onVisibilityChange),
    ];
  }

  function dispose() {
    for (const remove of listeners) remove();
    listeners = [];
    if (timer !== null) {
      clearTimer(timer);
      timer = null;
    }
  }

  return { track, count, flush, sessionEnded, allowStall, installGlobalHandlers, serviceWorkerError, dispose };
}
